import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface Statistics {
  total_revenue_ytd: number
  revenue_growth_pct: number
  total_customers: number
  customer_growth_pct: number
  net_profit_margin: number
  employee_count: number
}

interface KeyMetric {
  metric: string
  current: number
  target: number
  variance: number
  trend: string
}

interface BusinessUnit {
  unit: string
  revenue: number
  growth: number
  margin: number
  status: string
}

export default function ExecutiveDashboardExecPage() {
  const [statistics, setStatistics] = useState<Statistics | null>(null)
  const [metrics, setMetrics] = useState<KeyMetric[]>([])
  const [units, setUnits] = useState<BusinessUnit[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, metricsRes, unitsRes] = await Promise.all([
          fetch('/api/executivedashboard/statistics'),
          fetch('/api/executivedashboard/metrics'),
          fetch('/api/executivedashboard/businessunits')
        ])
        setStatistics(await statsRes.json())
        setMetrics(await metricsRes.json())
        setUnits(await unitsRes.json())
      } catch (error) {
        console.error('Error fetching data:', error)
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [])

  const getStatusBadge = (status: string) => {
    const colors: Record<string, string> = {
      'On Track': 'bg-green-100 text-green-800',
      'At Risk': 'bg-yellow-100 text-yellow-800',
      'Exceeding': 'bg-blue-100 text-blue-800',
      'Behind': 'bg-red-100 text-red-800'
    }
    return colors[status] || 'bg-gray-100 text-gray-800'
  }

  if (loading) {
    return <div className="p-8"><Header /><div className="mt-8">Loading Executive Dashboard...</div></div>
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="p-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">Executive Dashboard</h1>

        {statistics && (
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-blue-600">${(statistics.total_revenue_ytd / 1000000).toFixed(0)}M</div>
              <div className="text-sm text-gray-500">Revenue YTD</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-green-600">+{statistics.revenue_growth_pct}%</div>
              <div className="text-sm text-gray-500">Revenue Growth</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-purple-600">{statistics.total_customers.toLocaleString()}</div>
              <div className="text-sm text-gray-500">Customers</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-indigo-600">+{statistics.customer_growth_pct}%</div>
              <div className="text-sm text-gray-500">Customer Growth</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-orange-600">{statistics.net_profit_margin}%</div>
              <div className="text-sm text-gray-500">Profit Margin</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-teal-600">{statistics.employee_count}</div>
              <div className="text-sm text-gray-500">Employees</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b"><h2 className="text-lg font-semibold">Key Metrics</h2></div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Metric</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Current</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Target</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Variance</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {metrics.map((m, idx) => (
                    <tr key={idx} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium">{m.metric}</td>
                      <td className="px-4 py-3 text-gray-600">{m.current.toLocaleString()}</td>
                      <td className="px-4 py-3 text-gray-600">{m.target.toLocaleString()}</td>
                      <td className="px-4 py-3">
                        <span className={`${m.variance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {m.variance >= 0 ? '+' : ''}{m.variance}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b"><h2 className="text-lg font-semibold">Business Units</h2></div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Unit</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Revenue</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Growth</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {units.map((u, idx) => (
                    <tr key={idx} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium">{u.unit}</td>
                      <td className="px-4 py-3 text-gray-600">${(u.revenue / 1000000).toFixed(0)}M</td>
                      <td className="px-4 py-3 text-green-600">+{u.growth}%</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs ${getStatusBadge(u.status)}`}>
                          {u.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
